import React from 'react';
import Form from './Generic/Form';
import { TrashFill as RemoveIcon } from './Generic/Icons';
import { SingleInputColumn, TwoInputColumns } from './Generic/InputGrouping';
import AchievementsInput from './AchievementsInput';
import { bindOnChange } from '../Functions/Helper/OnChange';
import useRole from '../Provider/TableContext/RoleContext';
import { useLocation } from 'react-router';
import { ResponsibilitiesInput } from './ResponsibilitiesInput';
import useActionDialog from '../Provider/ActionDialogContext';
import "./ProfessionForm.scss";

const requiredParams = ["mcb_roletitle", "mcb_rolecompanyname", "mcb_starteddate"];

function ProfessionalAchievementInput({value, onChange}) {
    const addButtonProps= React.useMemo(() => ({ label: "Add the Draft Achievement"}), []);
    const updateButtonProps= React.useMemo(() => ({ label: "Add the Draft Achievement"}), []);
    const saveButtonProps= React.useMemo(() => ({ label: "Add the Draft Achievement"}), []);
    const _onChange = React.useMemo(() => bindOnChange(onChange, "achievements"), [onChange]);
    return (
        <>
            
            <AchievementsInput 
                values={value?.achievements || []}
                onChange={_onChange}
                addButtonProps={addButtonProps}
                updateButtonProps={updateButtonProps}
                saveButtonProps={saveButtonProps}
                newButtonLabel={"Draft an Achievement"}
                defaultNewCollapsed

            ><span className="h4 mb-3 mt-4 d-flex justify-content-between">Achievements</span></AchievementsInput>
        </>
    );
}

export default function ProfessionalForm({ 
    symbol, value, rolesData, onSubmit: _onSubmit, onCancel, onChange,
    ActionComponent, onRemove, formProps, loading
}) {
    const { hash  } = useLocation();
    const { roles } = useRole();
    const { confirm } = useActionDialog();
    const formRef = React.useRef(null);
    const onSubmit = React.useCallback(() => {
        if (typeof _onSubmit === "function") {
            _onSubmit(value);
        }
    }, [value, _onSubmit]);
    
    const forceCallback = React.useCallback(() => {
        return value?.responsibilities?.length > 0
        || requiredParams.some((alias) => value?.[alias]?.length && value[alias].trim()?.length > 0)
        || (typeof formProps?.forceCallback === "function" && formProps.forceCallback());
    }, [value, formProps]);

    const isValid = React.useCallback(() =>{ 
        return value?.responsibilities?.length > 0 && requiredParams.every((alias) => (
            value?.[alias]?.length && value[alias].trim()?.length > 0
        )) && (typeof formProps?.isValid !== "function" || formProps.isValid());
    }, [value, formProps]);

    React.useEffect(() => {
        if (value?.mcb_roleid === hash.slice(1)) {
            document.getElementById(value.mcb_roleid).scrollIntoView({behavior: "smooth"});
        }
    }, [hash, value?.mcb_roleid]);

    const onChangeOverride = React.useCallback((value, alias) => {
        onChange((oldValue) => {
            const result =  {
                ...oldValue,
                [alias]: value
            };
            (rolesData || roles || []).sort((a, b) => new Date(b.mcb_starteddate) - new Date(a.mcb_starteddate)).some(role => {
                if (
                    role.mcb_rolecompanyname
                    && role.mcb_rolehtml
                    && role.mcb_rolecompanyname === result.mcb_rolecompanyname
                ) {
                    result.mcb_rolehtml = role.mcb_rolehtml;
                    return true;
                } else {
                    return false;
                }
            });
            return result;
        });
    }, [onChange, roles, rolesData]);

    React.useEffect(() => {
        const companyNamesDataList = document.getElementById("unique-company-names");
        if (!companyNamesDataList) {
            const datalist = document.createElement("datalist");
            datalist.id = "unique-company-names";
            const uniqueCompanyNames = new Set();
            (rolesData || roles || []).forEach(role => {
                uniqueCompanyNames.add(role.mcb_rolecompanyname);
            });
            uniqueCompanyNames.add(value?.mcb_rolecompanyname);
            uniqueCompanyNames.forEach((companyName) => {
                if (companyName) {
                    const option = document.createElement("option");
                    option.value = companyName;
                    datalist.appendChild(option);
                }
            });
            document.body.prepend(datalist);

            return () => {
                datalist.remove();
            }
        }
    }, [roles, rolesData, value]);
    return (
        <div className="professional form d-flex flex-column gap-2" {...(value?.mcb_roleid?{id: value?.mcb_roleid}: {})}>
            <div style={{top: "-1.1rem", zIndex: "99999"}} className="h3 mb-0 d-flex justify-content-between align-items-center position-sticky pt-3 pb-2 mb-2 bg-light">
                <div>{value?.mcb_roletitle || "Add Title"}</div>
                <div className="text-end flex-grow-1 fs-smaller">
                    {value?.mcb_rolecompanyname}
                </div>
                {onRemove && (
                    <button
                        type="button"
                        className='btn btn-transparent text-danger fs-smaller'
                        onClick={async (...args) => {
                            if (await confirm("This could not be undone. Are you sure?")) {
                                onRemove(...args);
                            }
                        }}
                        > 
                        <RemoveIcon/>
                    </button>
                )}
            </div>
            <Form
                onSubmit={onSubmit}
                {...formProps}
                ref={formRef}
                symbol={symbol}
                forceCallback={forceCallback}
                isValid={isValid}
                className="d-flex flex-column gap-4 mb-4">
                <TwoInputColumns
                    state={value}
                    setState={onChange}
                    inputs={[
                        ['mcb_roletitle', 'text', 'Title', {required: true}],
                        ['mcb_rolecompanyname', 'text', 'Company Name', {required: true, list: "unique-company-names", onChangeOverride}],
                        ['mcb_starteddate', 'month', 'Started Date', {
                            required: true,
                            name: "mcb_starteddate",
                            maxDate: (!value.currentJob && value?.mcb_endeddate)? new Date(value?.mcb_endeddate) : new Date()
                        }],
                        ['mcb_endeddate', 'month', 'Ended Date', {
                            required: !value?.currentJob,
                            disabled: value?.currentJob,
                            minDate: value?.mcb_starteddate? new Date(value?.mcb_starteddate) : undefined,
                            maxDate: new Date(),
                            name: "mcb_endeddate",
                            disabledValue: "present"
                        }]
                    ]}
                />
                <SingleInputColumn
                    state={value}
                    setState={onChange}
                    inputs={[
                        ['currentJob', 'checkbox', <span className="fs-smaller">I am currently working in this role</span>],
                        ['mcb_rolehtml', 'richtext', (
                            <span><strong>Company Blurb</strong> (Some blurb about company) <em className='fs-smaller'>(max 350 char)</em></span>
                        ), { required: true, maxCharacterCount: 350 }],
                        ['mcb_challenge', 'richtext', (
                            <span><b>The Challenge</b> (Why were you appointed to the role?) <em className='fs-smaller'>(max 350 char)</em></span>
                        ), { required: true, maxCharacterCount: 350 }]
                    ]}
                />
            </Form>
            <ResponsibilitiesInput
                required
                key={loading? "loading-responsibilities" : "responsibilities"}
                value={value}
                onChange={onChange}
            />
            <ProfessionalAchievementInput
                key={loading? "loading-achievement" : "achievement"}
                value={value}
                onChange={onChange}
            />
            {ActionComponent && (
                <div key="actions">
                    <ActionComponent {...{onSubmit: () => {
                        if (formRef.current && formRef.current.reportValidity()) {
                            onSubmit();
                        }
                    }, onCancel, value, submitType: "button"}} />
                </div>
            )}
        </div>
    );
}